import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../../supabaseClient";
import Navigation from "../Navigation/Navigation";
import HomeFooter from "../HomePage/HomeFooter";
import stageIcon from "../../Assets/Images/mnt-podsused-logo.png";
import CardsIcon from "../../Assets/Icons/cards.png";
import LoaderBox from "../HomePage/LoaderBox";

export default function BelaTurnir() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const turnirScrollRef = useRef(null);
  const [zoom, setZoom] = useState(1);

  const getDistance = (touches) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const fetchBracket = async () => {
    const { data, error } = await supabase.from("bela_matches").select("*");

    if (error) {
      console.error("Greška pri dohvatu bela mečeva:", error);
    } else {
      setMatches(data || []);
    }
    setLoading(false);
  };
  // ================= POPRAVLJENO: STABILAN PINCH-TO-ZOOM BEZ SKAKANJA =================
  useEffect(() => {
    const container = turnirScrollRef.current;
    if (!container) return;

    let startDist = 0;
    let startZoom = zoom; // Uvijek uzmi svježi trenutni zoom na početku dodira
    let startCenter = { x: 0, y: 0 };

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        startCenter = {
          x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
          y: (e.touches[0].clientY + e.touches[1].clientY) / 2,
        };
        startDist = getDistance(e.touches);
        startZoom = zoom;
      }
    };

    const onTouchMove = (e) => {
      if (e.touches.length === 2 && startDist > 0) {
        if (e.cancelable) e.preventDefault();

        const currentDist = getDistance(e.touches);
        const factor = currentDist / startDist;

        // Izračunaj novi zoom na temelju početnog zooma kada je dodir počeo
        const nextZoom = Math.max(0.4, Math.min(startZoom * factor, 1.5));

        if (Math.abs(nextZoom - zoom) > 0.001) {
          // Mala tolerancija radi performansi
          const currentScrollLeft = container.scrollLeft;
          const currentScrollTop = container.scrollTop;
          const zoomRatio = nextZoom / zoom;

          setZoom(nextZoom);

          container.scrollLeft =
            (currentScrollLeft + startCenter.x) * zoomRatio - startCenter.x;
          container.scrollTop =
            (currentScrollTop + startCenter.y) * zoomRatio - startCenter.y;
        }
      }
    };

    const onTouchEnd = () => {
      startDist = 0;
    };

    container.addEventListener("touchstart", onTouchStart, { passive: false });
    container.addEventListener("touchmove", onTouchMove, { passive: false });
    container.addEventListener("touchend", onTouchEnd);

    return () => {
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onTouchMove);
      container.removeEventListener("touchend", onTouchEnd);
    };
  }, [zoom]); // Prati promjene zooma kako bi izračun omjera (zoomRatio) uvijek bio točan

  useEffect(() => {
    fetchBracket();

    const subscription = supabase
      .channel("bela_matches_realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "bela_matches" },
        () => {
          fetchBracket();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, []);

  const finalSlotId = 31;

  const totalStages = Math.floor(Math.log2(finalSlotId + 1));

  const getMatchBySlot = (slotId) => {
    const match = matches.find((m) => m.bracket_slot_id === slotId);
    return (
      match || {
        team_a_name: "?",
        team_b_name: "?",
        scorea: 0,
        scoreb: 0,
        table_number: null,
        match_time: null,
      }
    );
  };

  const getWinner = () => {
    const finale = getMatchBySlot(finalSlotId);
    if (!finale || !finale.is_completed) return "?";
    if (finale.scorea > finale.scoreb) return finale.team_a_name;
    if (finale.scoreb > finale.scorea) return finale.team_b_name;
    return "?";
  };

  const renderMatchCard = (slotId, sideClass) => {
    const m = getMatchBySlot(slotId);
    const imaInfo = m.table_number || m.match_time;
    return (
      <div key={slotId} className={`bela-match ${sideClass}`}>
        <div className="team">
          <h4>{m.team_a_name || "?"}</h4>
          <span>{m.scorea}</span>
        </div>
        <div className="team">
          <h4>{m.team_b_name || "?"}</h4>
          <span>{m.scoreb}</span>
        </div>{" "}
        {imaInfo && (
          <h5 className="match-info">
            {m.table_number ? `STOL ${m.table_number}` : ""}
            {m.table_number && m.match_time ? " - " : ""}
            {m.match_time ? m.match_time : ""}
          </h5>
        )}
        <div className="vertical-line"></div>
      </div>
    );
  };

  if (loading) return <LoaderBox text="Učitavanje bela turnira..." />;

  // ---- GENERIRANJE ID-jeva PO STUPCIMA (STAGES) ----
  const leftSidesColumns = [];
  const rightSidesColumns = [];

  // Idemo unazad od najveće faze (npr. Šesnaestina ili Osmina) prema Polufinalu
  let currentOffset = 0;
  for (let stage = totalStages; stage > 1; stage--) {
    const matchesInStageCount = Math.pow(2, stage - 1); // Koliko ukupno mečeva ima ta faza na cijelom turniru
    const halfCount = matchesInStageCount / 2; // Koliko mečeva ide na jednu stranu (lijevo ili desno)

    const leftSlots = [];
    const rightSlots = [];

    // Generiramo točne ID-jeve slotova za lijevu stranu stupca
    for (let i = 1; i <= halfCount; i++) {
      leftSlots.push(currentOffset + i);
    }
    // Generiramo točne ID-jeve slotova za desnu stranu stupca
    for (let i = 1; i <= halfCount; i++) {
      rightSlots.push(currentOffset + halfCount + i);
    }

    leftSidesColumns.push({ stageClass: `stage-${stage}`, slots: leftSlots });
    // Desnu stranu spremamo privremeno, ali moramo je zrcaliti (Polufinale -> najveća faza)
    rightSidesColumns.unshift({
      stageClass: `stage-${stage}`,
      slots: rightSlots,
    });

    currentOffset += matchesInStageCount;
  }

  return (
    <>
      <Navigation />
      <div className="bela-turnir-wrapper">
        <div className="championship-stage-header">
          <img src={CardsIcon} alt="Stage" className="stage-img" />
          <h3>Bela turnir</h3>
        </div>
        <div className="zoom-slider-container">
          <button
            type="button"
            className="zoom-reset-btn"
            onClick={() => setZoom(1)}
            title="Resetiraj na 100%"
          >
            {Math.round(zoom * 100)}%
          </button>
          <span className="zoom-label">
            <i className="fi fi-rs-zoom-in"></i>
          </span>
          <div className="slider-wrapper">
            <input
              type="range"
              min="0.4"
              max="1.5"
              step="0.05"
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="zoom-vertical-slider"
            />
          </div>
          <span className="zoom-label">
            <i className="fi fi-rs-zoom-out"></i>
          </span>
        </div>
        <div className="bela-turnir-scroll" ref={turnirScrollRef}>
          {/* PROMIJENJENO: Dodana CSS varijabla --zoom za rezanje praznog prostora u SCSS-u */}
          <div
            className="bela-turnir"
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: "left center",
              transition: "transform 0.05s ease-out",
              width: "max-content",
              height: "max-content",
              maxWidth: `calc(100% * ${zoom})`,
              display: "flex",
              alignItems: "center",
              gap: "0px",
            }}
          >
            {/* ================= LIJEVA STRANA (Automatski stupci) ================= */}
            <div className="left-side">
              {leftSidesColumns.map((col, index) => (
                <div
                  key={`left-col-${index}`}
                  className={`stage-column ${col.stageClass}`}
                >
                  {col.slots.map((id) => renderMatchCard(id, "match-left"))}
                </div>
              ))}
            </div>

            {/* ================= SREDIŠNJI DIO (FINALE) ================= */}
            {/* ================= SREDIŠNJI DIO (FINALE) ================= */}
            <div className="middle-side">
              <div className="final-content-wrapper">
                <div className="bela-match finale-box">
                  {(() => {
                    const m = getMatchBySlot(finalSlotId);
                    const imaFinalInfo = m.table_number || m.match_time;
                    return (
                      <>
                        <div className="team">
                          <h4>{m.team_a_name || "?"}</h4>
                          <span>{m.scorea}</span>
                        </div>
                        <div className="team">
                          <h4>{m.team_b_name || "?"}</h4>
                          <span>{m.scoreb}</span>
                        </div>

                        {/* Vrijeme i stol za finale */}
                        {imaFinalInfo && (
                          <h5 className="match-info-final">
                            {m.table_number ? `STOL ${m.table_number}` : ""}
                            {m.table_number && m.match_time ? " - " : ""}
                            {m.match_time ? m.match_time : ""}
                          </h5>
                        )}
                      </>
                    );
                  })()}
                </div>

                <div className="trophy-container">
                  <img src={stageIcon} alt="Trophy" className="stage-img" />
                  <h3>POBJEDNIK</h3>
                  <div className="winner-box">{getWinner()}</div>
                </div>
              </div>
            </div>

            {/* ================= DESNA STRANA (Automatski stupci - zrcalno) ================= */}
            <div className="right-side">
              {rightSidesColumns.map((col, index) => (
                <div
                  key={`right-col-${index}`}
                  className={`stage-column ${col.stageClass}`}
                >
                  {col.slots.map((id) => renderMatchCard(id, "match-right"))}
                </div>
              ))}
            </div>
            <div style={{ minWidth: "30px", height: "1px", flexShrink: 0 }} />
          </div>
        </div>

        <div className="scroll-hint">
          <div className="line"></div>
          <span>Povuci za faze</span>
          <div className="line"></div>
        </div>
      </div>
      <HomeFooter />
    </>
  );
}
